import type { ComponentPropsWithoutRef } from 'react';
import { Axis as VXAxis } from '@visx/axis';

import { useTheme } from '@simplywallst/ui-core';

import { VisxText } from '@/components/VisxText';

const getXY = (radius: number, theta: number) => ({
  x: radius * Math.sin(theta),
  y: -radius * Math.cos(theta),
});

export interface Props {
  numTicks: number;
  xScale: ComponentPropsWithoutRef<typeof VXAxis>['scale'];
  radius: number;
  offset: number;
  formatter: (value: number) => string;
  showTickMarks?: boolean;
  hideTickById?: number;
}

const Axis = ({
  numTicks,
  xScale,
  radius,
  offset,
  formatter,
  showTickMarks = true,
  hideTickById,
}: Props) => {
  const theme = useTheme();
  return (
    <VXAxis scale={xScale} numTicks={numTicks}>
      {(axis) => {
        return (
          <g key={`gauge-axis-parent-${axis.ticks.length}`}>
            {axis.ticks.map((tick) => {
              const textPos = getXY(radius + offset, tick.from.x);
              const lineFrom = getXY(radius, tick.from.x);
              const lineTo = getXY(radius - offset, tick.from.x);
              const value = formatter(
                typeof tick.value === 'number'
                  ? tick.value
                  : tick.value.valueOf()
              );
              if (hideTickById && tick.index === hideTickById) return null;
              return (
                <g key={`gauge-axis-${value}`}>
                  <VisxText
                    dx={textPos.x}
                    dy={textPos.y}
                    lineHeight="1em"
                    verticalAnchor="middle"
                    textAnchor="middle"
                    typography="tinyStrong"
                    fill={theme.color.white}
                  >
                    {value}
                  </VisxText>
                  {showTickMarks && (
                    <line
                      x1={lineFrom.x}
                      y1={lineFrom.y}
                      x2={lineTo.x}
                      y2={lineTo.y}
                      stroke="#151B24"
                      strokeWidth="2"
                      strokeOpacity="0.5"
                    />
                  )}
                </g>
              );
            })}
          </g>
        );
      }}
    </VXAxis>
  );
};

export default Axis;
